// src/Public/PrivacyPolicy.js
import React from "react";
import { Link } from "react-router-dom";
import config from "../config.json";
import "../styles/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const appName = config.apptitle || "Pocket App - Expense tracker";

  return (
    <div className="privacy-page-container">
      {/* Hero Banner Card */}
      <div className="privacy-hero-card">
        <div className="privacy-hero-badge">
          <i className="fi fi-rr-shield-check"></i>
          <span>Privacy & Security</span>
        </div>
        <h1 className="privacy-hero-title">Privacy Policy</h1>
        <p className="privacy-hero-subtitle">
          Your personal finances and expense records deserve complete transparency and uncompromised privacy.
        </p>
        <span className="privacy-hero-date">
          <i className="fi fi-rr-calendar me-1"></i> Last updated: March 2025
        </span>
      </div>

      {/* Trust Highlights Grid */}
      <div className="privacy-trust-grid">
        <div className="privacy-trust-item">
          <div className="privacy-trust-icon coral">
            <i className="fi fi-rr-lock"></i>
          </div>
          <div className="privacy-trust-title">Local Guest Mode</div>
          <p className="privacy-trust-desc">
            Track expenses with 100% on-device local storage. No cloud required.
          </p>
        </div>

        <div className="privacy-trust-item">
          <div className="privacy-trust-icon mauve">
            <i className="fi fi-rr-fingerprint"></i>
          </div>
          <div className="privacy-trust-title">Device Biometrics</div>
          <p className="privacy-trust-desc">
            Face ID & Touch ID stay exclusively in your device's Secure Enclave.
          </p>
        </div>

        <div className="privacy-trust-item">
          <div className="privacy-trust-icon mauve">
            <i className="fi fi-rr-ban"></i>
          </div>
          <div className="privacy-trust-title">Zero Data Selling</div>
          <p className="privacy-trust-desc">
            We never sell, rent, or share your financial records with advertisers.
          </p>
        </div>

        <div className="privacy-trust-item">
          <div className="privacy-trust-icon coral">
            <i className="fi fi-rr-key"></i>
          </div>
          <div className="privacy-trust-title">Passwordless Auth</div>
          <p className="privacy-trust-desc">
            One-time verification codes (OTP) protect accounts without risky stored passwords.
          </p>
        </div>
      </div>

      {/* Card 1: Overview */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon">
            <i className="fi fi-rr-info"></i>
          </div>
          <h2 className="privacy-card-title">1. Overview & Commitment</h2>
        </div>
        <div className="privacy-card-body">
          <p>
            This Privacy Policy describes our policies regarding the collection, use, and disclosure of information when you use <strong>{appName}</strong> (the "Application" or "Service").
          </p>
          <p>
            We believe that managing your personal finances should be private, convenient, and empowering. By using the Service, you consent to the handling of information in accordance with this Privacy Policy.
          </p>
        </div>
      </div>

      {/* Card 2: Information We Collect */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon coral">
            <i className="fi fi-rr-document"></i>
          </div>
          <h2 className="privacy-card-title">2. Information We Collect</h2>
        </div>
        <div className="privacy-card-body">
          <p>We only collect the minimum amount of data required to provide you with seamless expense tracking:</p>

          <ul className="privacy-list">
            <li className="privacy-list-item">
              <strong>Account & Profile Information:</strong> If you choose to sign in, we collect your email address and profile name to deliver one-time verification passcodes (OTP) and synchronize your shared expenses across devices.
            </li>
            <li className="privacy-list-item">
              <strong>Expense & Financial Entries:</strong> The monetary amounts, dates, categories, payment methods, and notes you input into the expense ledger. When running in Guest Mode, this information is stored locally on your device only.
            </li>
            <li className="privacy-list-item">
              <strong>Face ID & Biometric Verification:</strong> When you enable Face ID, Touch ID, or fingerprint authentication on supported iOS and Android devices, authentication is executed natively by your operating system's Secure Enclave (Apple <code>LocalAuthentication</code> / Android <code>BiometricPrompt</code>). <strong>We never capture, receive, or store your biometric data.</strong>
            </li>
            <li className="privacy-list-item">
              <strong>Device & Diagnostics Data:</strong> Basic technical diagnostics (such as device OS version and browser type) to diagnose crash reports and optimize viewport responsiveness.
            </li>
          </ul>
        </div>
      </div>

      {/* Card 3: How We Use Information */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon">
            <i className="fi fi-rr-chart-pie"></i>
          </div>
          <h2 className="privacy-card-title">3. How We Use Your Information</h2>
        </div>
        <div className="privacy-card-body">
          <p>We utilize your data solely for the following operational needs:</p>
          <ul className="privacy-list">
            <li className="privacy-list-item">To calculate balances, category summaries, monthly budgets, and analytics.</li>
            <li className="privacy-list-item">To synchronize shared expenses and balances among verified group members.</li>
            <li className="privacy-list-item">To verify authentication requests via secure, temporary email one-time passwords (OTP).</li>
            <li className="privacy-list-item">To maintain service stability, security integrity, and bug remediation.</li>
          </ul>
          <p>
            <span className="privacy-feature-badge">No Ads</span> We do not display third-party advertisements or use ad trackers in {appName}.
          </p>
        </div>
      </div>

      {/* Card 4: Security & Storage */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon coral">
            <i className="fi fi-rr-shield"></i>
          </div>
          <h2 className="privacy-card-title">4. Security & Data Protection</h2>
        </div>
        <div className="privacy-card-body">
          <p>
            The security of your information is paramount. We implement multi-layered industry safeguards:
          </p>
          <ul className="privacy-list">
            <li className="privacy-list-item">
              <strong>Encryption in Transit:</strong> All data transmissions between your device and our cloud servers are encrypted using modern Transport Layer Security (TLS/HTTPS).
            </li>
            <li className="privacy-list-item">
              <strong>Passwordless Architecture:</strong> We avoid the risks of credential leaks by utilizing passwordless email OTP verification instead of storing static passwords.
            </li>
            <li className="privacy-list-item">
              <strong>Device Sandbox Isolation:</strong> Guest mode data is isolated within your device's browser sandbox (<code>localStorage</code>), inaccessible to outside websites.
            </li>
          </ul>
        </div>
      </div>

      {/* Card 5: Your Rights & Data Deletion */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon">
            <i className="fi fi-rr-user-gear"></i>
          </div>
          <h2 className="privacy-card-title">5. Your Data Control & Deletion</h2>
        </div>
        <div className="privacy-card-body">
          <p>
            You retain complete control over your personal records:
          </p>
          <ul className="privacy-list">
            <li className="privacy-list-item">
              <strong>Edit or Delete Entries:</strong> You may edit or delete any expense item, category, or note at any time directly inside the app.
            </li>
            <li className="privacy-list-item">
              <strong>Clear Guest Data:</strong> In Guest Mode, you can purge all records instantly by clearing your browser/app cache or selecting sign out.
            </li>
            <li className="privacy-list-item">
              <strong>Account Erasure:</strong> Registered users can request permanent deletion of their account and all associated shared records by contacting our support team.
            </li>
          </ul>
        </div>
      </div>

      {/* Card 6: Children's Privacy & Updates */}
      <div className="privacy-card">
        <div className="privacy-card-header">
          <div className="privacy-card-icon coral">
            <i className="fi fi-rr-users"></i>
          </div>
          <h2 className="privacy-card-title">6. Children & Policy Updates</h2>
        </div>
        <div className="privacy-card-body">
          <p>
            Our Service is not directed toward children under 13 years of age. We do not knowingly collect personal information from children under 13.
          </p>
          <p>
            We may periodically update this Privacy Policy to reflect app enhancements or legal guidelines. When modifications occur, the "Last updated" date at the top of this page will be revised.
          </p>
        </div>
      </div>

      {/* Contact Card */}
      <div className="privacy-contact-card">
        <div className="privacy-contact-icon">
          <i className="fi fi-rr-envelope"></i>
        </div>
        <h3 className="privacy-contact-title">Have Questions or Concerns?</h3>
        <p className="privacy-contact-desc">
          If you have questions regarding this Privacy Policy or wish to exercise your data rights, we are here to help.
        </p>
        <div className="privacy-btn-group">
          <span className="privacy-btn-primary">
            <i className="fi fi-rr-envelope"></i> {config.supportemail}
          </span>
          <Link to="/public/contact" className="privacy-btn-secondary">
            <i className="fi fi-rr-headset"></i> Contact Support
          </Link>
        </div>
      </div>

      {/* Footer Note */}
      <div className="privacy-footer-note">
        <div>&copy; {new Date().getFullYear()} {appName}. All rights reserved.</div>
        <div className="mt-1">
          <Link to="/">Home</Link> &bull; <Link to="/public/contact">Support</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
